import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

// All factors are expressed in bits, so the source of truth is a single
// BigDecimal-free path: the user's input is converted to bits, then every
// other unit is derived by dividing. JavaScript numbers (IEEE 754) handle
// up to ~2^53 safely, which covers ~1 PiB in bits — well past the typical
// developer use case for this kind of converter.
const SI = [
  { key: "b", labelKey: "byte_size_unit_b", bitsPerUnit: 8 },
  { key: "kb", labelKey: "byte_size_unit_kb", bitsPerUnit: 8 * 1e3 },
  { key: "mb", labelKey: "byte_size_unit_mb", bitsPerUnit: 8 * 1e6 },
  { key: "gb", labelKey: "byte_size_unit_gb", bitsPerUnit: 8 * 1e9 },
  { key: "tb", labelKey: "byte_size_unit_tb", bitsPerUnit: 8 * 1e12 },
  { key: "pb", labelKey: "byte_size_unit_pb", bitsPerUnit: 8 * 1e15 },
];

const IEC = [
  { key: "kib", labelKey: "byte_size_unit_kib", bitsPerUnit: 8 * 1024 },
  { key: "mib", labelKey: "byte_size_unit_mib", bitsPerUnit: 8 * 1024 ** 2 },
  { key: "gib", labelKey: "byte_size_unit_gib", bitsPerUnit: 8 * 1024 ** 3 },
  { key: "tib", labelKey: "byte_size_unit_tib", bitsPerUnit: 8 * 1024 ** 4 },
  { key: "pib", labelKey: "byte_size_unit_pib", bitsPerUnit: 8 * 1024 ** 5 },
];

const BITS = [
  { key: "bit", labelKey: "byte_size_unit_bit", bitsPerUnit: 1 },
  { key: "kbit", labelKey: "byte_size_unit_kbit", bitsPerUnit: 1e3 },
  { key: "mbit", labelKey: "byte_size_unit_mbit", bitsPerUnit: 1e6 },
  { key: "gbit", labelKey: "byte_size_unit_gbit", bitsPerUnit: 1e9 },
];

const ALL_UNITS = [...SI, ...IEC, ...BITS];

const findUnit = (key) => ALL_UNITS.find((u) => u.key === key);

// Recognized unit suffixes for inline input (e.g. "1.5 GB", "500 MiB",
// "8 kbit"). Keys are case-insensitive; values map to entries in ALL_UNITS.
const UNIT_ALIASES = {
  b: "b", byte: "b", bytes: "b",
  kb: "kb", kilobyte: "kb", kilobytes: "kb",
  mb: "mb", megabyte: "mb", megabytes: "mb",
  gb: "gb", gigabyte: "gb", gigabytes: "gb",
  tb: "tb", terabyte: "tb", terabytes: "tb",
  pb: "pb", petabyte: "pb", petabytes: "pb",
  kib: "kib", kibibyte: "kib", kibibytes: "kib",
  mib: "mib", mebibyte: "mib", mebibytes: "mib",
  gib: "gib", gibibyte: "gib", gibibytes: "gib",
  tib: "tib", tebibyte: "tib", tebibytes: "tib",
  pib: "pib", pebibyte: "pib", pebibytes: "pib",
  bit: "bit", bits: "bit",
  kbit: "kbit", kilobit: "kbit", kilobits: "kbit", kbps: "kbit",
  mbit: "mbit", megabit: "mbit", megabits: "mbit", mbps: "mbit",
  gbit: "gbit", gigabit: "gbit", gigabits: "gbit", gbps: "gbit",
};

// Split raw input into a numeric value and (optionally) a unit token.
// Accepts "1.5", "1.5 GB", "500MiB", "8 kbit", "1,024 kb" (comma-stripped).
// Returns { value, detectedUnit, invalid } where invalid=true means the
// caller should surface the standard invalid-input error.
const parseValueWithUnit = (raw) => {
  const trimmed = raw.trim().replace(/,/g, "");
  if (trimmed === "") return { value: null, detectedUnit: null, invalid: false };
  const match = trimmed.match(
    /^(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)\s*([A-Za-z]+)?$/,
  );
  if (!match) return { value: null, detectedUnit: null, invalid: true };
  const value = Number(match[1]);
  if (!Number.isFinite(value)) {
    return { value: null, detectedUnit: null, invalid: true };
  }
  if (!match[2]) return { value, detectedUnit: null, invalid: false };
  const detectedUnit = UNIT_ALIASES[match[2].toLowerCase()];
  if (!detectedUnit) return { value: null, detectedUnit: null, invalid: true };
  return { value, detectedUnit, invalid: false };
};

const formatNumber = (n) => {
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "0";
  const abs = Math.abs(n);
  // Integer-looking values: render without trailing zeros, with thousands grouping.
  if (abs >= 1 && Number.isInteger(n)) {
    return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
  }
  // Very small or very large magnitudes: switch to exponential to stay readable.
  if (abs !== 0 && (abs < 1e-4 || abs >= 1e15)) {
    return n.toExponential(6).replace(/\.?0+e/, "e");
  }
  // Otherwise show enough fractional digits to keep precision without noise.
  const digits = abs >= 1 ? 6 : 8;
  return n
    .toLocaleString("en-US", { maximumFractionDigits: digits })
    .replace(/(\.\d*?)0+$/, "$1")
    .replace(/\.$/, "");
};

const humanReadable = (bytes, mode) => {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  const base = mode === "iec" ? 1024 : 1000;
  const labels =
    mode === "iec"
      ? ["B", "KiB", "MiB", "GiB", "TiB", "PiB"]
      : ["B", "kB", "MB", "GB", "TB", "PB"];
  if (bytes < 1) return `${formatNumber(bytes)} B`;
  let i = Math.floor(Math.log(bytes) / Math.log(base));
  if (i >= labels.length) i = labels.length - 1;
  const value = bytes / base ** i;
  return `${formatNumber(value)} ${labels[i]}`;
};

const ByteSizeConverter = () => {
  const { t } = useTranslation();
  const [rawInput, setRawInput] = useState("1");
  const [unit, setUnit] = useState("mb");

  const parsed = useMemo(() => parseValueWithUnit(rawInput), [rawInput]);
  const effectiveUnit = parsed.detectedUnit ?? unit;

  const { bits, error } = useMemo(() => {
    if (parsed.value === null) {
      if (parsed.invalid) return { bits: null, error: "byte_size_error_invalid" };
      return { bits: null, error: null };
    }
    if (parsed.value < 0) {
      return { bits: null, error: "byte_size_error_invalid" };
    }
    const u = findUnit(effectiveUnit);
    return { bits: parsed.value * u.bitsPerUnit, error: null };
  }, [parsed, effectiveUnit]);

  const outputs = useMemo(() => {
    if (bits === null) return {};
    const result = {};
    ALL_UNITS.forEach((u) => {
      result[u.key] = bits / u.bitsPerUnit;
    });
    return result;
  }, [bits]);

  const bytes = bits === null ? null : bits / 8;

  const copy = async (text) => {
    if (!text) {
      toast.error(t("nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(String(text));
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const clear = () => setRawInput("");

  const renderUnitCard = (u) => (
    <div
      key={u.key}
      className="bg-indigo-50 p-3 rounded-md border border-indigo-200"
    >
      <div className="flex items-center justify-between gap-3">
        <strong className="text-indigo-700">{t(u.labelKey)}</strong>
        <StandardButton
          onClick={() => copy(outputs[u.key])}
          type="secondary"
          variant="outlined"
          size="small"
          disabled={bits === null}
        >
          {t("copy_button")}
        </StandardButton>
      </div>
      <p className="break-all mt-2 text-gray-800 font-mono text-sm whitespace-pre-wrap">
        {bits === null ? (
          <em className="text-gray-400 not-italic">—</em>
        ) : (
          formatNumber(outputs[u.key])
        )}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t("byte_size_title")}
              </h1>
              <p className="text-gray-600 mt-1">{t("byte_size_intro")}</p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                {t("byte_size_input_label")}
              </label>
              <div className="flex flex-wrap gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  className="flex-1 min-w-[12rem] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono text-sm"
                  placeholder={t("byte_size_input_placeholder")}
                  value={rawInput}
                  onChange={(e) => setRawInput(e.target.value)}
                  autoComplete="off"
                  spellCheck="false"
                />
                <select
                  value={effectiveUnit}
                  onChange={(e) => setUnit(e.target.value)}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none text-sm bg-white"
                  aria-label={t("byte_size_unit_select_label")}
                >
                  <optgroup label={t("byte_size_group_decimal")}>
                    {SI.map((u) => (
                      <option key={u.key} value={u.key}>
                        {t(u.labelKey)}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label={t("byte_size_group_binary")}>
                    {IEC.map((u) => (
                      <option key={u.key} value={u.key}>
                        {t(u.labelKey)}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label={t("byte_size_group_bits")}>
                    {BITS.map((u) => (
                      <option key={u.key} value={u.key}>
                        {t(u.labelKey)}
                      </option>
                    ))}
                  </optgroup>
                </select>
                <StandardButton
                  onClick={clear}
                  type="secondary"
                  variant="outlined"
                  size="small"
                  disabled={!rawInput}
                >
                  {t("byte_size_clear")}
                </StandardButton>
              </div>
              {parsed.detectedUnit && (
                <p className="text-xs text-gray-500">
                  {t("byte_size_detected_unit", {
                    unit: t(findUnit(parsed.detectedUnit).labelKey),
                  })}
                </p>
              )}
            </div>

            {error && (
              <p className="text-sm text-red-600">{t(error)}</p>
            )}

            {!error && bits !== null && (
              <>
                <div className="bg-white p-4 rounded-md border border-indigo-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <div className="text-xs uppercase text-gray-500">
                      {t("byte_size_human_label")}
                    </div>
                    <div className="text-lg font-mono text-indigo-700">
                      <span>{humanReadable(bytes, "si")}</span>
                      <span className="text-gray-400 mx-2">·</span>
                      <span>{humanReadable(bytes, "iec")}</span>
                    </div>
                  </div>
                  <StandardButton
                    onClick={() => copy(humanReadable(bytes, "iec"))}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>

                <div>
                  <h2 className="text-sm font-semibold text-gray-700 mb-2">
                    {t("byte_size_group_decimal")}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {SI.map(renderUnitCard)}
                  </div>
                </div>

                <div>
                  <h2 className="text-sm font-semibold text-gray-700 mb-2">
                    {t("byte_size_group_binary")}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {IEC.map(renderUnitCard)}
                  </div>
                </div>

                <div>
                  <h2 className="text-sm font-semibold text-gray-700 mb-2">
                    {t("byte_size_group_bits")}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {BITS.map(renderUnitCard)}
                  </div>
                </div>
              </>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("byte_size_note_si_vs_iec")}</p>
              <p>{t("byte_size_note_bits")}</p>
              <p>{t("byte_size_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default ByteSizeConverter;
